import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { MutationCache, QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import {
  asyncStoragePersister,
  shouldDehydrateQuery,
} from "@/lib/query-persister";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { AppZeroProvider } from "@/zero/provider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { MainLayout } from "@/components/layout/MainLayout";
import AuthPage from "./pages/AuthPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DevicePage from "./pages/DevicePage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import ProjectCalendarPage from "./pages/ProjectCalendarPage";
import ProjectGroupsPage from "./pages/ProjectGroupsPage";
import ProjectGroupDetailPage from "./pages/ProjectGroupDetailPage";
import CompaniesPage from "./pages/CompaniesPage";
import CompanyDetailPage from "./pages/CompanyDetailPage";
import PeoplePage from "./pages/PeoplePage";
import PersonDetailPage from "./pages/PersonDetailPage";
import CrewPlanningPage from "./pages/CrewPlanningPage";
import EquipmentPage from "./pages/EquipmentPage";
import EquipmentDetailPage from "./pages/EquipmentDetailPage";
import EquipmentTagPage from "./pages/EquipmentTagPage";
import EquipmentUntaggedPage from "./pages/EquipmentUntaggedPage";
import EquipmentTagsPage from "./pages/EquipmentTagsPage";
import VehiclesPage from "./pages/VehiclesPage";
import VehicleDetailPage from "./pages/VehicleDetailPage";
import VehiclePlanningPage from "./pages/VehiclePlanningPage";
import StudiosPage from "./pages/StudiosPage";
import StudioDetailPage from "./pages/StudioDetailPage";
import ConfigEditorPage from "./pages/ConfigEditorPage";
import MeetingNotesPage from "./pages/MeetingNotesPage";
import MeetingNoteDetailPage from "./pages/MeetingNoteDetailPage";
import TasksPage from "./pages/TasksPage";
import NetworkCoveragePage from "./pages/NetworkCoveragePage";
import SimCardsPage from "./pages/SimCardsPage";
import CloudMediaPage from "./pages/CloudMediaPage";
import CloudComputersPage from "./pages/CloudComputersPage";
import DefaultFunctionsPage from "./pages/DefaultFunctionsPage";
import AdminMaintenancePage from "./pages/AdminMaintenancePage";
import AdminMailCatcherPage from "./pages/AdminMailCatcherPage";
import SettingsPage from "./pages/SettingsPage";
import HRPage from "./pages/HRPage";
import MyBalancePage from "./pages/MyBalancePage";
import QuoteTemplatesPage from "./pages/QuoteTemplatesPage";
import DashboardPage from "./pages/DashboardPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import GlobalDocsPage from "./pages/GlobalDocsPage";
import FinancePage from "./pages/FinancePage";
import NotFound from "./pages/NotFound";
import { NewVersionBanner } from "@/components/NewVersionBanner";
import { friendlyMutationError } from "@/lib/error-utils";
import { toast } from "sonner";

const FIVE_SECONDS = 1000 * 5;
const TWENTY_FOUR_HOURS = 1000 * 60 * 60 * 24;

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError) return;
      toast.error(friendlyMutationError(error));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: FIVE_SECONDS,
      gcTime: TWENTY_FOUR_HOURS,
      retry: (failureCount, error) => {
        if (!navigator.onLine) return false;
        // Don't retry auth or not-found errors
        if (error instanceof Error) {
          const msg = error.message;
          if (msg.includes("401") || msg.includes("403") || msg.includes("404"))
            return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

const persistOptions = {
  persister: asyncStoragePersister,
  maxAge: TWENTY_FOUR_HOURS,
  dehydrateOptions: {
    shouldDehydrateQuery,
  },
};

const App = () => (
  <ErrorBoundary>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
    >
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <NewVersionBanner />
        <BrowserRouter>
          <AuthProvider>
            <AppZeroProvider>
              <Routes>
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route
                  path="/device"
                  element={
                    <ProtectedRoute>
                      <DevicePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<DashboardPage />} />
                  <Route path="my-balance" element={<MyBalancePage />} />
                  <Route path="projects" element={<ProjectsPage />} />
                  <Route
                    path="projects/:id/*"
                    element={<ProjectDetailPage />}
                  />
                  <Route
                    path="projects/calendar"
                    element={<ProjectCalendarPage />}
                  />
                  <Route
                    path="projects/groups"
                    element={
                      <ProtectedRoute requireEmployee>
                        <ProjectGroupsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="projects/groups/:id"
                    element={
                      <ProtectedRoute requireEmployee>
                        <ProjectGroupDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="companies"
                    element={
                      <ProtectedRoute requireEmployee>
                        <CompaniesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="companies/:id"
                    element={
                      <ProtectedRoute requireEmployee>
                        <CompanyDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="people"
                    element={
                      <ProtectedRoute requireEmployee>
                        <PeoplePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="people/new"
                    element={
                      <ProtectedRoute requireEmployee>
                        <PersonDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="people/:id"
                    element={
                      <ProtectedRoute requireEmployee>
                        <PersonDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="people/employees"
                    element={
                      <ProtectedRoute requireEmployee>
                        <PeoplePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="people/employees/new"
                    element={
                      <ProtectedRoute requireEmployee>
                        <PersonDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="people/freelancers"
                    element={
                      <ProtectedRoute requireEmployee>
                        <PeoplePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="people/freelancers/new"
                    element={
                      <ProtectedRoute requireEmployee>
                        <PersonDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="crew-planning"
                    element={
                      <ProtectedRoute requireEmployee>
                        <CrewPlanningPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="equipment"
                    element={
                      <ProtectedRoute requireEmployee>
                        <EquipmentPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="vehicles"
                    element={
                      <ProtectedRoute requireEmployee>
                        <VehiclesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="vehicles/planning"
                    element={
                      <ProtectedRoute requireEmployee>
                        <VehiclePlanningPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="vehicles/:id"
                    element={
                      <ProtectedRoute requireEmployee>
                        <VehicleDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="equipment/tags"
                    element={
                      <ProtectedRoute requireEmployee>
                        <EquipmentTagsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="equipment/untagged"
                    element={
                      <ProtectedRoute requireEmployee>
                        <EquipmentUntaggedPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="equipment/tag/:tagId"
                    element={
                      <ProtectedRoute requireEmployee>
                        <EquipmentTagPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="equipment/:id"
                    element={
                      <ProtectedRoute requireEmployee>
                        <EquipmentDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="studios"
                    element={
                      <ProtectedRoute requireEmployee>
                        <StudiosPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="studios/:id"
                    element={
                      <ProtectedRoute requireEmployee>
                        <StudioDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="studios/:studioId/config/:configId"
                    element={
                      <ProtectedRoute requireEmployee>
                        <ConfigEditorPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="cloud-media"
                    element={
                      <ProtectedRoute requireEmployee>
                        <CloudMediaPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="cloud-computers"
                    element={
                      <ProtectedRoute requireEmployee>
                        <CloudComputersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="docs"
                    element={
                      <ProtectedRoute requireEmployee>
                        <GlobalDocsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="finance"
                    element={
                      <ProtectedRoute requireEmployee>
                        <FinancePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="finance/invoices/:invoiceId"
                    element={
                      <ProtectedRoute requireEmployee>
                        <FinancePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="meeting-notes"
                    element={
                      <ProtectedRoute requireEmployee>
                        <MeetingNotesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="meeting-notes/management"
                    element={
                      <ProtectedRoute requireEmployee>
                        <MeetingNotesPage managementOnly />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="meeting-notes/:id"
                    element={
                      <ProtectedRoute requireEmployee>
                        <MeetingNoteDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="tasks"
                    element={
                      <ProtectedRoute requireEmployee>
                        <TasksPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="network-coverage"
                    element={
                      <ProtectedRoute requireEmployee>
                        <NetworkCoveragePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="sims"
                    element={
                      <ProtectedRoute requireEmployee>
                        <SimCardsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/maintenance"
                    element={
                      <ProtectedRoute requireAdmin>
                        <AdminMaintenancePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/mail-catcher"
                    element={
                      <ProtectedRoute requireAdmin>
                        <AdminMailCatcherPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/settings"
                    element={
                      <ProtectedRoute requireAdmin>
                        <AdminSettingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/default-functions"
                    element={
                      <ProtectedRoute requireAdmin>
                        <DefaultFunctionsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="hr"
                    element={
                      <ProtectedRoute requireAdmin>
                        <HRPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="settings"
                    element={
                      <ProtectedRoute requireEmployee>
                        <SettingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/quote-templates"
                    element={
                      <ProtectedRoute requireAdmin>
                        <QuoteTemplatesPage />
                      </ProtectedRoute>
                    }
                  />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AppZeroProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </PersistQueryClientProvider>
  </ErrorBoundary>
);

export default App;
